import { http } from '@/http/axios';
import { submitInteraction } from '@/http/interactionWriteApi';
import { getPublicRead } from '@/http/publicReadApi';
import type {
  CreateIntegrationApiKeyInput,
  EvaluationQuestion,
  EvaluationQuestionInput,
  EvaluationTemplate,
  EvaluationTemplateInput,
  IndexItem,
  IndexItemBulkReplaceInput,
  IndexItemInput,
  IntegrationApiKeyCreatedDto,
  IntegrationApiKeyDto,
  Moderator,
  ModeratorInput,
  OfferLinkTestDto,
  OfferTemplateContent,
  OfferTemplate,
  OfferTemplateContentInput,
  OfferTemplateInput,
  ParticipationStatusBulkInput,
  Presenter,
  PresenterInput,
  PublicSurveySubmission,
  PublicWebinarMe,
  PublicWebinarSession,
  PublicWebinarSubscriptionForm,
  QuitPage,
  QuitPageInput,
  QuitPageReason,
  QuitPageReasonInput,
  QuizAnswerOption,
  QuizAnswerOptionInput,
  QuizQuestion,
  QuizQuestionInput,
  QuizSubmission,
  QuizTemplate,
  QuizTemplateInput,
  AddManualChatMessageInput,
  EvergreenChatConfiguration,
  EvergreenConfiguration,
  UpdateEvergreenChatConfigInput,
  EvergreenConfigurationInput,
  EvergreenTimelineEvent,
  EvergreenTimelineEventInput,
  EvergreenScheduledInteraction,
  EvergreenScheduledInteractionInput,
  EvergreenManualPurchaseNotificationInput,
  EvergreenMp4PartUploadUrl,
  EvergreenMp4UploadCompleteInput,
  EvergreenMp4UploadInit,
  EvergreenMp4UploadInitInput,
  EvergreenMp4UploadPartsRequest,
  EvergreenMp4UploadStatusDto,
  EvergreenSourcesInput,
  ReplayConfiguration,
  ReplayConfigurationInput,
  ResourceUploadInitInput,
  SessionInvite,
  SlideNotesBulkInput,
  SlideNoteUpdateInput,
  SlideUploadInit,
  SlideUploadInitInput,
  SharedResource,
  SharedResourceDownloadUrl,
  SharedResourceFileUploadInitInput,
  SharedResourceLinkInput,
  SharedResourcePage,
  SharedResourcePublic,
  SharedResourceUploadInit,
  Speaker,
  SpeakerInput,
  SurveyAnswerOption,
  SurveyAnswerOptionInput,
  SurveyQuestion,
  SurveyQuestionInput,
  SurveyTemplate,
  SurveyTemplateInput,
  TechnicalAuthUser,
  ThankYouPage,
  ThankYouPageInput,
  WaitingRoomPage,
  WaitingRoomPageInput,
  WebhookDto,
  WebhookInput,
  Webinar,
  WebinarDuplicationResult,
  WebinarIndex,
  WebinarParticipationStatusConfigDto,
  WebinarResource,
  WebinarResourceFinalizeInput,
  WebinarResourceUploadInit,
  WebinarSession,
  WebinarSessionAttendee,
  SessionAttendeesImportResult,
  SessionAttendeesImportStatus,
  WebinarSessionConfiguration,
  WebinarSessionDetail,
  WebinarSessionConfigurationInput,
  WebinarSlide,
  WebinarSlideFinalizeInput,
  WebinarSlideNote,
  WebinarSubscriptionDto,
  WebinarSubscriptionPage,
  WebinarSubscriptionPageInput,
  WebinarForEvergreen,
  WebinarIntegrationTrigger,
  WebinarIntegrationTriggerInput,
  WebinarLanguage,
  EvergreenTestRun,
  EvergreenTestRuntime,
} from '@/features/webinar/types';
import type {
  WorkspaceTeamInvitation,
  WorkspaceTeamInviteInput,
  WorkspaceTeamPage,
} from '@/features/workspace/types';

/** Réponse upload-init API parfois en snake_case (resource_key, upload_url). */
function normalizeWebinarResourceUploadInit(data: unknown): WebinarResourceUploadInit {
  if (data === null || typeof data !== 'object') {
    return { resourceKey: '', uploadUrl: '' };
  }
  const d = data as Record<string, unknown>;
  const resourceKey = d.resourceKey ?? d.resource_key;
  const uploadUrl = d.uploadUrl ?? d.upload_url;
  return {
    resourceKey: typeof resourceKey === 'string' ? resourceKey : String(resourceKey ?? ''),
    uploadUrl: typeof uploadUrl === 'string' ? uploadUrl : String(uploadUrl ?? ''),
  };
}

export type CreateWebinarInput = {
  title: string;
  shortDescription: string;
  description: string;
  transmissionMode?: 'NATIVE' | 'YOUTUBE';
  youtubeUrl?: string;
};

export type UpdateWebinarInput = CreateWebinarInput & {
  displayParticipantsMinThreshold?: number;
  bannedWords?: string[];
  chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
  displayParticipantsCountEnabled?: boolean;
  accessCodeEnabled?: boolean;
  language?: WebinarLanguage;
};

export type DuplicateWebinarInput = {
  sourceWebinarId: number;
  newTitle: string;
};

export type PageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};
type WebinarPageResponse = PageResponse<Webinar>;
type WebinarSessionPageResponse = PageResponse<WebinarSession>;
type WebinarSessionAttendeePageResponse = PageResponse<WebinarSessionAttendee>;
type OfferTemplatePageResponse = PageResponse<OfferTemplate>;
type SurveyTemplatePageResponse = PageResponse<SurveyTemplate>;
type QuizTemplatePageResponse = PageResponse<QuizTemplate>;
type WebinarResourcePageResponse = PageResponse<WebinarResource>;
type WebinarSlidePageResponse = PageResponse<WebinarSlide>;
type SpeakerPageResponse = PageResponse<Speaker>;
type ModeratorPageResponse = PageResponse<Moderator>;
type PresenterPageResponse = PageResponse<Presenter>;

// Timeout dédié au long polling de duplication : doit rester au-dessus de l'attente serveur
// du back (25s) pour ne pas avorter la requête avant sa réponse.
const DUPLICATION_STATUS_REQUEST_TIMEOUT_MS = 35000;

export const webinarService = {
  create: async (workspaceId: number, input: CreateWebinarInput): Promise<number | null> => {
    const response = await http.post(`/workspaces/${workspaceId}/webinars`, input);
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  // Initialise la duplication asynchrone : le back répond 202 avec l'identifiant du clone
  // et son statut initial (INITIALIZATION). La copie des assets se poursuit côté back.
  duplicate: async (
    workspaceId: number,
    input: DuplicateWebinarInput,
  ): Promise<WebinarDuplicationResult | null> => {
    const response = await http.post(`/workspaces/${workspaceId}/webinars/duplicate`, input);
    const data = response.data as Partial<WebinarDuplicationResult> | undefined;
    if (data?.webinarId == null || data.status == null) {
      return null;
    }
    return { webinarId: Number(data.webinarId), status: data.status };
  },
  // Suivi long polling de la duplication : le back bloque côté serveur tant que le clone est
  // INITIALIZATION puis renvoie le statut courant (DRAFT, DUPLICATION_FAILED, ou encore INITIALIZATION
  // si son propre timeout d'attente est atteint, auquel cas le front rappelle cet endpoint).
  getDuplicationStatus: async (
    workspaceId: number,
    webinarId: number,
  ): Promise<WebinarDuplicationResult> => {
    const response = await http.get(
      `/workspaces/${workspaceId}/webinars/${webinarId}/duplication-status`,
      // Le back bloque côté serveur jusqu'à 25s par appel : on relève le timeout axios
      // (20s par défaut) au-dessus de cette attente, sinon la requête serait avortée à tort.
      { timeout: DUPLICATION_STATUS_REQUEST_TIMEOUT_MS },
    );
    const data = response.data as WebinarDuplicationResult;
    return { webinarId: Number(data.webinarId), status: data.status };
  },
  update: (workspaceId: number, id: number, input: UpdateWebinarInput) =>
    http.put(`/workspaces/${workspaceId}/webinars/${id}`, input).then(() => undefined),
  importBannedWordsFromCsv: (workspaceId: number, id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .post(`/workspaces/${workspaceId}/webinars/${id}/banned-words/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => undefined);
  },
  addBannedWords: (workspaceId: number, id: number, words: string[]) =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${id}/banned-words`, { words })
      .then(() => undefined),
  removeBannedWords: (workspaceId: number, id: number, words: string[]) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${id}/banned-words`, { data: { words } })
      .then(() => undefined),
  delete: (workspaceId: number, id: number) =>
    http.delete(`/workspaces/${workspaceId}/webinars/${id}`).then(() => undefined),
  unpublish: (workspaceId: number, id: number) =>
    http.post(`/workspaces/${workspaceId}/webinars/${id}/unpublish`).then(() => undefined),
  publish: (workspaceId: number, id: number) =>
    http.post(`/workspaces/${workspaceId}/webinars/${id}/publish`).then(() => undefined),
  getById: (workspaceId: number, id: number): Promise<Webinar> =>
    http.get<Webinar>(`/workspaces/${workspaceId}/webinars/${id}`).then((r) => r.data),
  getAll: (workspaceId: number, page = 0, size = 10): Promise<PageResponse<Webinar>> =>
    http
      .get<WebinarPageResponse>(`/workspaces/${workspaceId}/webinars`, { params: { page, size } })
      .then((r) => r.data),

  getAvailableWebinars: (
    workspaceId: number,
    page = 0,
    size = 35,
  ): Promise<PageResponse<WebinarForEvergreen>> =>
    http
      .get<PageResponse<WebinarForEvergreen>>(`/workspaces/${workspaceId}/available-webinars`, {
        params: { page, size },
      })
      .then((r) => r.data),

  getInvites: (workspaceId: number, webinarId: number): Promise<SessionInvite[]> =>
    http
      .get<SessionInvite[]>(`/workspaces/${workspaceId}/webinars/${webinarId}/invites`)
      .then((r) => r.data),

  getPublicSession: (webinarId: number, sessionIdentifier: string): Promise<PublicWebinarSession> =>
    getPublicRead<PublicWebinarSession>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}`,
    ),

  getPublicSubscriptionForm: (webinarId: number): Promise<PublicWebinarSubscriptionForm> =>
    getPublicRead<PublicWebinarSubscriptionForm>(`/public/webinars/${webinarId}/subscription-form`),

  getPublicIndex: (webinarId: number, authorization: string): Promise<WebinarIndex> =>
    getPublicRead<WebinarIndex>(`/public/webinars/${webinarId}/index`, { authorization }),

  getPublicWebinarMe: (webinarId: number, authorization: string): Promise<PublicWebinarMe> =>
    http
      .get<PublicWebinarMe>(`/public/webinars/${webinarId}/me`, {
        headers: { Authorization: authorization },
      })
      .then((r) => r.data),

  getPublicWaitingRoomSurvey: (webinarId: number, authorization: string): Promise<SurveyTemplate> =>
    getPublicRead<SurveyTemplate>(`/public/webinars/${webinarId}/waiting-room-survey`, {
      authorization,
    }),

  getPublicWaitingRoomQuiz: (webinarId: number, authorization: string): Promise<QuizTemplate> =>
    getPublicRead<QuizTemplate>(`/public/webinars/${webinarId}/waiting-room-quiz`, {
      authorization,
    }),
  validatePublicSessionCode: (
    webinarId: number,
    sessionIdentifier: string,
    token: string,
    code: string,
  ): Promise<void> =>
    http
      .post<void>(`/public/webinars/${webinarId}/sessions/${sessionIdentifier}/validate-code`, {
        token,
        code,
      })
      .then((r) => r.data),
  submitPublicWaitingRoomSurvey: (
    webinarId: number,
    data: {
      questions: Array<{
        question: string;
        answers: string[];
      }>;
    },
    authorization: string,
  ): Promise<void> =>
    http
      .post(`/public/webinars/${webinarId}/waiting-room-survey`, data, {
        headers: { Authorization: authorization },
      })
      .then(() => undefined),

  getPublicWaitingRoomPage: (webinarId: number, authorization: string): Promise<WaitingRoomPage> =>
    getPublicRead<WaitingRoomPage>(`/public/webinars/${webinarId}/waiting-room`, {
      authorization,
    }),

  registerPublicWebinar: (data: {
    sessionIdentifier: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  }): Promise<WebinarSubscriptionDto> =>
    http.post<WebinarSubscriptionDto>(`/public/webinars/register`, data).then((r) => r.data),

  registerPublicWebinarReplay: (
    webinarId: number,
    sessionIdentifier: string,
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phoneNumber?: string;
    },
  ): Promise<WebinarSubscriptionDto> =>
    http
      .post<WebinarSubscriptionDto>(
        `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/replay/register`,
        data,
      )
      .then((r) => r.data),

  submitPublicSurvey: (
    webinarId: number,
    templateId: number,
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string;
      }>;
    },
    authorization: string,
  ): Promise<void> =>
    submitInteraction('survey', 'post', webinarId, templateId, authorization, data).then(
      () => undefined,
    ),

  submitPublicSurveyPut: (
    webinarId: number,
    templateId: number,
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string;
      }>;
    },
    authorization: string,
  ): Promise<void> =>
    submitInteraction('survey', 'put', webinarId, templateId, authorization, data).then(
      () => undefined,
    ),

  submitPublicQuiz: (
    webinarId: number,
    templateId: number,
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    },
    authorization: string,
  ): Promise<void> =>
    submitInteraction('quiz', 'post', webinarId, templateId, authorization, data).then(
      () => undefined,
    ),

  submitPublicQuizPut: (
    webinarId: number,
    templateId: number,
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    },
    authorization: string,
  ): Promise<void> =>
    submitInteraction('quiz', 'put', webinarId, templateId, authorization, data).then(
      () => undefined,
    ),

  getMyPublicSurveySubmission: (
    webinarId: number,
    templateId: number,
    authorization: string,
  ): Promise<PublicSurveySubmission | null> =>
    http
      .get<PublicSurveySubmission | null>(
        `/public/webinars/${webinarId}/surveys/${templateId}/my-submission`,
        {
          headers: { Authorization: authorization },
        },
      )
      .then((r) => r.data),

  getMyPublicQuizSubmission: (
    webinarId: number,
    templateId: number,
    authorization: string,
  ): Promise<QuizSubmission | null> =>
    http
      .get<QuizSubmission | null>(
        `/public/webinars/${webinarId}/quizzes/${templateId}/my-submission`,
        {
          headers: { Authorization: authorization },
        },
      )
      .then((r) => r.data),

  listSessions: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<WebinarSessionPageResponse> =>
    http
      .get<WebinarSessionPageResponse>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions`,
        { params: { page, size } },
      )
      .then((r) => r.data),

  getSession: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<WebinarSession> =>
    http
      .get<WebinarSession>(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}`)
      .then((r) => r.data),

  getSessionDetail: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<WebinarSessionDetail> =>
    http
      .get<WebinarSessionDetail>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}`,
      )
      .then((r) => r.data),

  deleteSession: (workspaceId: number, webinarId: number, sessionId: number): Promise<void> =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}`)
      .then(() => {}),

  listSessionAttendees: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    page = 0,
    size = 10,
    search?: string,
  ): Promise<WebinarSessionAttendeePageResponse> => {
    const trimmedSearch = search?.trim();
    return http
      .get<WebinarSessionAttendeePageResponse>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/attendees`,
        { params: { page, size, ...(trimmedSearch ? { search: trimmedSearch } : {}) } },
      )
      .then((r) => r.data);
  },

  deleteSessionAttendee: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    subscriptionId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/attendees/subscriptions/${subscriptionId}`,
      )
      .then(() => undefined),

  getSessionAttendeesExport: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<Blob> =>
    http
      .get<Blob>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/attendees/export`,
        {
          responseType: 'blob',
          headers: {
            Accept:
              'text/csv, application/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*',
          },
        },
      )
      .then((r) => r.data),

  importSessionAttendees: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    file: File,
  ): Promise<SessionAttendeesImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .post<SessionAttendeesImportResult>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/attendees/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  initiateSessionAttendeesImport: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    file: File,
  ): Promise<SessionAttendeesImportStatus> => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .post<SessionAttendeesImportStatus>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/attendees/import-async`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  getSessionAttendeesImportStatus: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    importId: number,
  ): Promise<SessionAttendeesImportStatus> =>
    http
      .get<SessionAttendeesImportStatus>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/attendees/imports/${importId}`,
      )
      .then((r) => r.data),

  getSessionChatExport: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<Blob> =>
    http
      .get<Blob>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/export`,
        { responseType: 'blob' },
      )
      .then((r) => r.data),

  getWebinarSession: (
    workspaceId: number,
    webinarId: number,
  ): Promise<WebinarSessionConfiguration> =>
    http
      .get<WebinarSessionConfiguration>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/webinar-session-configurations`,
      )
      .then((r) => r.data),
  updateSessionConfiguration: (
    workspaceId: number,
    webinarId: number,
    input: WebinarSessionConfigurationInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/webinar-session-configurations`, input)
      .then(() => undefined),

  // Webinar Index API
  getIndex: (workspaceId: number, webinarId: number): Promise<WebinarIndex> =>
    http
      .get<WebinarIndex>(`/workspaces/${workspaceId}/webinars/${webinarId}/index`)
      .then((r) => r.data),
  listIndexItems: (workspaceId: number, webinarId: number): Promise<IndexItem[]> =>
    http
      .get<IndexItem[]>(`/workspaces/${workspaceId}/webinars/${webinarId}/index/items`)
      .then((r) => r.data),
  createIndexItem: (workspaceId: number, webinarId: number, input: IndexItemInput) =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/index/items`, input)
      .then(() => undefined),
  updateIndexItem: (
    workspaceId: number,
    webinarId: number,
    itemId: number,
    input: IndexItemInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/index/items/${itemId}`, input)
      .then(() => undefined),
  deleteIndexItem: (workspaceId: number, webinarId: number, itemId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/index/items/${itemId}`)
      .then(() => undefined),
  replaceIndexItems: (workspaceId: number, webinarId: number, input: IndexItemBulkReplaceInput) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/index/items`, input)
      .then(() => undefined),

  getSubscriptionPage: (workspaceId: number, webinarId: number): Promise<WebinarSubscriptionPage> =>
    http
      .get<WebinarSubscriptionPage>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/subscription-page`,
      )
      .then((r) => r.data),
  updateSubscriptionPage: (
    workspaceId: number,
    webinarId: number,
    input: WebinarSubscriptionPageInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/subscription-page`, input)
      .then(() => undefined),
  uploadBackgroundImage: (workspaceId: number, webinarId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/subscription-page/background-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then(() => undefined);
  },

  getReplayConfiguration: (workspaceId: number, webinarId: number): Promise<ReplayConfiguration> =>
    http
      .get<ReplayConfiguration>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/replay-configuration`,
      )
      .then((r) => r.data),

  getEvergreenConfiguration: (
    workspaceId: number,
    webinarId: number,
  ): Promise<EvergreenConfiguration> =>
    http
      .get<EvergreenConfiguration>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/configuration`,
      )
      .then((r) => r.data),

  updateEvergreenConfiguration: (
    workspaceId: number,
    webinarId: number,
    input: EvergreenConfigurationInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/configuration`, input)
      .then(() => undefined),

  getEvergreenTimeline: (
    workspaceId: number,
    webinarId: number,
  ): Promise<EvergreenTimelineEvent[]> =>
    http
      .get<EvergreenTimelineEvent[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/timeline`,
      )
      .then((r) => r.data),

  replaceEvergreenTimeline: (
    workspaceId: number,
    webinarId: number,
    input: EvergreenTimelineEventInput[],
  ): Promise<EvergreenTimelineEvent[]> =>
    http
      .put<EvergreenTimelineEvent[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/timeline`,
        input,
      )
      .then((r) => r.data),

  deleteEvergreenTimeline: (workspaceId: number, webinarId: number, ids: number[]): Promise<void> =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/timeline`, {
        params: { ids },
      })
      .then(() => undefined),

  createEvergreenScheduledInteraction: (
    workspaceId: number,
    webinarId: number,
    input: EvergreenScheduledInteractionInput,
  ): Promise<EvergreenScheduledInteraction> =>
    http
      .post<EvergreenScheduledInteraction>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/interactions`,
        input,
      )
      .then((r) => r.data),

  updateEvergreenScheduledInteraction: (
    workspaceId: number,
    webinarId: number,
    interactionId: number,
    input: EvergreenScheduledInteractionInput,
  ): Promise<EvergreenScheduledInteraction> =>
    http
      .put<EvergreenScheduledInteraction>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/interactions/${interactionId}`,
        input,
      )
      .then((r) => r.data),

  deleteEvergreenScheduledInteraction: (
    workspaceId: number,
    webinarId: number,
    interactionId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/interactions/${interactionId}`,
      )
      .then(() => undefined),

  moveEvergreenPurchaseNotification: (
    workspaceId: number,
    webinarId: number,
    eventId: number,
    startOffsetSeconds: number,
  ): Promise<EvergreenTimelineEvent> =>
    http
      .put<EvergreenTimelineEvent>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/purchase-notifications/${eventId}`,
        { startOffsetSeconds },
      )
      .then((r) => r.data),

  createEvergreenManualPurchaseNotification: (
    workspaceId: number,
    webinarId: number,
    input: EvergreenManualPurchaseNotificationInput,
  ): Promise<EvergreenTimelineEvent> =>
    http
      .post<EvergreenTimelineEvent>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/purchase-notifications`,
        input,
      )
      .then((r) => r.data),

  updateEvergreenManualPurchaseNotification: (
    workspaceId: number,
    webinarId: number,
    eventId: number,
    input: EvergreenManualPurchaseNotificationInput,
  ): Promise<EvergreenTimelineEvent> =>
    http
      .put<EvergreenTimelineEvent>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/purchase-notifications/${eventId}/manual`,
        input,
      )
      .then((r) => r.data),

  deleteEvergreenManualPurchaseNotification: (
    workspaceId: number,
    webinarId: number,
    eventId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/purchase-notifications/${eventId}/manual`,
      )
      .then(() => undefined),

  initEvergreenMp4Upload: (
    workspaceId: number,
    webinarId: number,
    input: EvergreenMp4UploadInitInput,
  ): Promise<EvergreenMp4UploadInit> =>
    http
      .post<EvergreenMp4UploadInit>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/mp4-uploads`,
        input,
      )
      .then((r) => r.data),

  getEvergreenMp4UploadPartUrls: (
    workspaceId: number,
    webinarId: number,
    uploadId: number,
    input: EvergreenMp4UploadPartsRequest,
  ): Promise<EvergreenMp4PartUploadUrl[]> =>
    http
      .post<EvergreenMp4PartUploadUrl[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/mp4-uploads/${uploadId}/parts`,
        input,
      )
      .then((r) => r.data),

  completeEvergreenMp4Upload: (
    workspaceId: number,
    webinarId: number,
    uploadId: number,
    input: EvergreenMp4UploadCompleteInput,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/mp4-uploads/${uploadId}/complete`,
        input,
      )
      .then(() => undefined),

  getEvergreenMp4UploadStatus: (
    workspaceId: number,
    webinarId: number,
    uploadId: number,
  ): Promise<EvergreenMp4UploadStatusDto> =>
    http
      .get<EvergreenMp4UploadStatusDto>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/mp4-uploads/${uploadId}`,
      )
      .then((r) => r.data),

  updateEvergreenSources: (
    workspaceId: number,
    webinarId: number,
    input: EvergreenSourcesInput,
  ): Promise<EvergreenConfiguration> =>
    http
      .put<EvergreenConfiguration>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/sources`,
        input,
      )
      .then((r) => r.data),

  importEvergreenChat: (workspaceId: number, webinarId: number, file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/chat/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => undefined);
  },

  getEvergreenChatConfiguration: (
    workspaceId: number,
    webinarId: number,
  ): Promise<EvergreenChatConfiguration> =>
    http
      .get<EvergreenChatConfiguration>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/chat/configuration`,
      )
      .then((r) => r.data),

  updateEvergreenChatConfiguration: (
    workspaceId: number,
    webinarId: number,
    input: UpdateEvergreenChatConfigInput,
  ): Promise<void> =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/chat/configuration`, input)
      .then(() => undefined),

  addEvergreenChatMessage: (
    workspaceId: number,
    webinarId: number,
    input: AddManualChatMessageInput,
  ): Promise<void> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/chat/messages`, input)
      .then(() => undefined),

  updateEvergreenChatMessage: (
    workspaceId: number,
    webinarId: number,
    messageId: number,
    input: AddManualChatMessageInput,
  ): Promise<void> =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/chat/messages/${messageId}`,
        input,
      )
      .then(() => undefined),

  deleteEvergreenChatMessages: (workspaceId: number, webinarId: number): Promise<void> =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/chat/messages`)
      .then(() => undefined),

  updateReplayConfiguration: (
    workspaceId: number,
    webinarId: number,
    input: ReplayConfigurationInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/replay-configuration`, input)
      .then(() => undefined),

  // Waiting Room Page API
  getWaitingRoomPage: (workspaceId: number, webinarId: number): Promise<WaitingRoomPage> =>
    http
      .get<WaitingRoomPage>(`/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page`)
      .then((r) => r.data),
  updateWaitingRoomPage: (workspaceId: number, webinarId: number, input: WaitingRoomPageInput) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page`, input)
      .then(() => undefined),
  deleteWaitingRoomPageMainAsset: (workspaceId: number, webinarId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page/main-asset`)
      .then(() => undefined),
  uploadWaitingRoomPageBackgroundImage: (workspaceId: number, webinarId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page/background-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then(() => undefined);
  },
  uploadWaitingRoomPageImage: (workspaceId: number, webinarId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => undefined);
  },
  initWaitingRoomPageVideoUpload: (
    workspaceId: number,
    webinarId: number,
    input: ResourceUploadInitInput,
  ): Promise<WebinarResourceUploadInit> =>
    http
      .post<WebinarResourceUploadInit>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page/video/upload-init`,
        {
          fileName: input.fileName,
          mimeType: input.mimeType,
          sizeBytes: input.sizeBytes,
        },
        {
          params: {
            fileName: input.fileName,
            mimeType: input.mimeType,
            sizeBytes: input.sizeBytes,
          },
        },
      )
      .then((r) => normalizeWebinarResourceUploadInit(r.data)),
  finalizeWaitingRoomPageVideoUpload: (
    workspaceId: number,
    webinarId: number,
    input: WebinarResourceFinalizeInput,
  ) =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/waiting-room-page/video/finalize`,
        {
          resourceKey: input.resourceKey,
          fileName: input.fileName,
          mimeType: input.mimeType,
          sizeBytes: input.sizeBytes,
          pageCount: input.pageCount,
          checksumSha256: input.checksumSha256,
          position: input.position,
        },
        {
          params: {
            resourceKey: input.resourceKey,
            fileName: input.fileName,
            mimeType: input.mimeType,
            sizeBytes: input.sizeBytes,
          },
        },
      )
      .then(() => undefined),
  // Thank You Page API
  getThankYouPage: (workspaceId: number, webinarId: number): Promise<ThankYouPage> =>
    http
      .get<ThankYouPage>(`/workspaces/${workspaceId}/webinars/${webinarId}/thank-you-page`)
      .then((r) => r.data),
  updateThankYouPage: (workspaceId: number, webinarId: number, input: ThankYouPageInput) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/thank-you-page`, input)
      .then(() => undefined),
  uploadThankYouPageImage: (workspaceId: number, webinarId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/thank-you-page/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => undefined);
  },
  deleteThankYouPageImage: (workspaceId: number, webinarId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/thank-you-page/image`)
      .then(() => undefined),

  // Webinar Slide API
  listSlides: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<WebinarSlidePageResponse> =>
    http
      .get<WebinarSlidePageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/slides`, {
        params: { page, size },
      })
      .then((r) => r.data),
  getSlide: (workspaceId: number, webinarId: number, slideId: number): Promise<WebinarSlide> =>
    http
      .get<WebinarSlide>(`/workspaces/${workspaceId}/webinars/${webinarId}/slides/${slideId}`)
      .then((r) => r.data),
  generateSlideUploadUrl: (
    workspaceId: number,
    webinarId: number,
    input: SlideUploadInitInput,
  ): Promise<SlideUploadInit> =>
    http
      .post<SlideUploadInit>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/slides/upload-url`,
        input,
      )
      .then((r) => r.data),
  finalizeSlideUpload: async (
    workspaceId: number,
    webinarId: number,
    input: WebinarSlideFinalizeInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/slides`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  deleteSlide: (workspaceId: number, webinarId: number, slideId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/slides/${slideId}`)
      .then(() => undefined),
  listSlideNotes: (
    workspaceId: number,
    webinarId: number,
    slideId: number,
  ): Promise<WebinarSlideNote[]> =>
    http
      .get<WebinarSlideNote[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/slides/${slideId}/notes`,
      )
      .then((r) => r.data),
  bulkUpsertSlideNotes: (
    workspaceId: number,
    webinarId: number,
    slideId: number,
    input: SlideNotesBulkInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/slides/${slideId}/notes`, input)
      .then(() => undefined),
  updateSlideNote: (
    workspaceId: number,
    webinarId: number,
    slideId: number,
    pageNumber: number,
    input: SlideNoteUpdateInput,
  ) =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/slides/${slideId}/notes/${pageNumber}`,
        input,
      )
      .then(() => undefined),

  // Webinar Resource API
  listResources: (
    workspaceId: number,
    webinarId: number,
    page: number,
    size: number = 10,
  ): Promise<WebinarResourcePageResponse> =>
    http
      .get<WebinarResourcePageResponse>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/resources`,
        {
          params: { page, size },
        },
      )
      .then((r) => r.data),
  initResourceUpload: (
    workspaceId: number,
    webinarId: number,
    input: ResourceUploadInitInput,
  ): Promise<WebinarResourceUploadInit> =>
    http
      .post<WebinarResourceUploadInit>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/resources/upload-init`,
        input,
      )
      .then((r) => r.data),
  finalizeResourceUpload: (
    workspaceId: number,
    webinarId: number,
    input: WebinarResourceFinalizeInput,
  ): Promise<number> =>
    http
      .post<number>(`/workspaces/${workspaceId}/webinars/${webinarId}/resources/finalize`, input)
      .then((r) => r.data),
  deleteResource: (workspaceId: number, webinarId: number, resourceId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/resources/${resourceId}`)
      .then(() => undefined),

  // Offer Template API
  listOfferTemplates: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<OfferTemplatePageResponse> =>
    http
      .get<OfferTemplatePageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/offers`, {
        params: { page, size },
      })
      .then((r) => r.data),
  getOfferTemplate: (
    workspaceId: number,
    webinarId: number,
    offerId: number,
  ): Promise<OfferTemplate> =>
    http
      .get<OfferTemplate>(`/workspaces/${workspaceId}/webinars/${webinarId}/offers/${offerId}`)
      .then((r) => r.data),
  createOfferTemplate: async (
    workspaceId: number,
    webinarId: number,
    input: OfferTemplateInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/offers`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateOfferTemplate: (
    workspaceId: number,
    webinarId: number,
    offerId: number,
    input: OfferTemplateInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/offers/${offerId}`, input)
      .then(() => undefined),
  deleteOfferTemplate: (workspaceId: number, webinarId: number, offerId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/offers/${offerId}`)
      .then(() => undefined),
  upsertOfferTemplateContent: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    contentType: string,
    input: OfferTemplateContentInput,
  ) =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${templateId}/contents/${contentType}`,
        input,
      )
      .then(() => undefined),
  deleteOfferTemplateContent: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    contentType: string,
  ) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${templateId}/contents/${contentType}`,
      )
      .then(() => undefined),
  uploadOfferTemplateContentImage: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    contentType: string,
    file: File,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${templateId}/contents/${contentType}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then(() => undefined);
  },
  deleteOfferTemplateContentImage: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    contentType: string,
  ) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${templateId}/contents/${contentType}/image`,
      )
      .then(() => undefined),

  // Survey Template API
  listSurveyTemplates: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<SurveyTemplatePageResponse> =>
    http
      .get<SurveyTemplatePageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/surveys`, {
        params: { page, size },
      })
      .then((r) => r.data),
  getSurveyTemplate: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
  ): Promise<SurveyTemplate> =>
    http
      .get<SurveyTemplate>(`/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}`)
      .then((r) => r.data),
  createSurveyTemplate: async (
    workspaceId: number,
    webinarId: number,
    input: SurveyTemplateInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/surveys`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateSurveyTemplate: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    input: SurveyTemplateInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}`, input)
      .then(() => undefined),
  deleteSurveyTemplate: (workspaceId: number, webinarId: number, templateId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}`)
      .then(() => undefined),

  listSurveyQuestions: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
  ): Promise<SurveyQuestion[]> =>
    http
      .get<SurveyQuestion[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions`,
      )
      .then((r) => r.data),
  createSurveyQuestion: async (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    input: SurveyQuestionInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateSurveyQuestion: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    input: SurveyQuestionInput,
  ) =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions/${questionId}`,
        input,
      )
      .then(() => undefined),
  deleteSurveyQuestion: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
  ) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions/${questionId}`,
      )
      .then(() => undefined),

  listSurveyAnswerOptions: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
  ): Promise<SurveyAnswerOption[]> =>
    http
      .get<SurveyAnswerOption[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions/${questionId}/options`,
      )
      .then((r) => r.data),
  createSurveyAnswerOption: async (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    input: SurveyAnswerOptionInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions/${questionId}/options`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateSurveyAnswerOption: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    optionId: number,
    input: SurveyAnswerOptionInput,
  ) =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions/${questionId}/options/${optionId}`,
        input,
      )
      .then(() => undefined),
  deleteSurveyAnswerOption: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    optionId: number,
  ) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/questions/${questionId}/options/${optionId}`,
      )
      .then(() => undefined),

  // Quit Page API
  getQuitPage: (workspaceId: number, webinarId: number): Promise<QuitPage> =>
    http
      .get<QuitPage>(`/workspaces/${workspaceId}/webinars/${webinarId}/quit-page`)
      .then((r) => r.data),
  upsertQuitPage: (workspaceId: number, webinarId: number, input: QuitPageInput) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/quit-page`, input)
      .then(() => undefined),
  listQuitPageReasons: (workspaceId: number, webinarId: number): Promise<QuitPageReason[]> =>
    http
      .get<QuitPageReason[]>(`/workspaces/${workspaceId}/webinars/${webinarId}/quit-page/reasons`)
      .then((r) => r.data),
  createQuitPageReason: async (
    workspaceId: number,
    webinarId: number,
    input: QuitPageReasonInput,
  ): Promise<number> => {
    const response = await http.post<number>(
      `/workspaces/${workspaceId}/webinars/${webinarId}/quit-page/reasons`,
      input,
    );
    return response.data;
  },
  updateQuitPageReason: (
    workspaceId: number,
    webinarId: number,
    reasonId: number,
    input: QuitPageReasonInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/quit-page/reasons/${reasonId}`, input)
      .then(() => undefined),
  deleteQuitPageReason: (workspaceId: number, webinarId: number, reasonId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/quit-page/reasons/${reasonId}`)
      .then(() => undefined),
  uploadQuitPageReasonImage: (
    workspaceId: number,
    webinarId: number,
    reasonId: number,
    file: File,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quit-page/reasons/${reasonId}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then(() => undefined);
  },
  deleteQuitPageReasonImage: (workspaceId: number, webinarId: number, reasonId: number) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quit-page/reasons/${reasonId}/image`,
      )
      .then(() => undefined),

  // Quiz Template API
  listQuizTemplates: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<QuizTemplatePageResponse> =>
    http
      .get<QuizTemplatePageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/quizzes`, {
        params: { page, size },
      })
      .then((r) => r.data),
  getQuizTemplate: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
  ): Promise<QuizTemplate> =>
    http
      .get<QuizTemplate>(`/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}`)
      .then((r) => r.data),
  createQuizTemplate: async (
    workspaceId: number,
    webinarId: number,
    input: QuizTemplateInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateQuizTemplate: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    input: QuizTemplateInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}`, input)
      .then(() => undefined),
  deleteQuizTemplate: (workspaceId: number, webinarId: number, templateId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}`)
      .then(() => undefined),

  listQuizQuestions: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
  ): Promise<QuizQuestion[]> =>
    http
      .get<QuizQuestion[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions`,
      )
      .then((r) => r.data),
  createQuizQuestion: async (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    input: QuizQuestionInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateQuizQuestion: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    input: QuizQuestionInput,
  ) =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions/${questionId}`,
        input,
      )
      .then(() => undefined),
  deleteQuizQuestion: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
  ) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions/${questionId}`,
      )
      .then(() => undefined),

  listQuizAnswerOptions: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
  ): Promise<QuizAnswerOption[]> =>
    http
      .get<QuizAnswerOption[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions/${questionId}/options`,
      )
      .then((r) => r.data),
  createQuizAnswerOption: async (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    input: QuizAnswerOptionInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions/${questionId}/options`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateQuizAnswerOption: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    optionId: number,
    input: QuizAnswerOptionInput,
  ) =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions/${questionId}/options/${optionId}`,
        input,
      )
      .then(() => undefined),
  deleteQuizAnswerOption: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    questionId: number,
    optionId: number,
  ) =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/questions/${questionId}/options/${optionId}`,
      )
      .then(() => undefined),

  // Speaker API
  listSpeakers: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<SpeakerPageResponse> =>
    http
      .get<SpeakerPageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/speakers`, {
        params: { page, size },
      })
      .then((r) => r.data),
  getSpeaker: (workspaceId: number, webinarId: number, speakerId: number): Promise<Speaker> =>
    http
      .get<Speaker>(`/workspaces/${workspaceId}/webinars/${webinarId}/speakers/${speakerId}`)
      .then((r) => r.data),
  createSpeaker: async (
    workspaceId: number,
    webinarId: number,
    input: SpeakerInput,
    file?: File | null,
  ): Promise<number | null> => {
    let response;
    if (file) {
      // Création avec photo : multipart (part `input` en JSON + part `file`)
      const formData = new FormData();
      formData.append('input', new Blob([JSON.stringify(input)], { type: 'application/json' }));
      formData.append('file', file);
      response = await http.post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/speakers`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    } else {
      response = await http.post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/speakers`,
        input,
      );
    }
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateSpeaker: (workspaceId: number, webinarId: number, speakerId: number, input: SpeakerInput) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/speakers/${speakerId}`, input)
      .then(() => undefined),
  uploadSpeakerPicture: (workspaceId: number, webinarId: number, speakerId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/speakers/${speakerId}/picture`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then(() => undefined);
  },
  deleteSpeakerPicture: (workspaceId: number, webinarId: number, speakerId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/speakers/${speakerId}/picture`)
      .then(() => undefined),
  deleteSpeaker: (workspaceId: number, webinarId: number, speakerId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/speakers/${speakerId}`)
      .then(() => undefined),

  // Moderator API
  listModerators: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 10,
  ): Promise<ModeratorPageResponse> =>
    http
      .get<ModeratorPageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/moderators`, {
        params: { page, size },
      })
      .then((r) => r.data),
  getModerator: (workspaceId: number, webinarId: number, moderatorId: number): Promise<Moderator> =>
    http
      .get<Moderator>(`/workspaces/${workspaceId}/webinars/${webinarId}/moderators/${moderatorId}`)
      .then((r) => r.data),
  createModerator: async (
    workspaceId: number,
    webinarId: number,
    input: ModeratorInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/moderators`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateModerator: (
    workspaceId: number,
    webinarId: number,
    moderatorId: number,
    input: ModeratorInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/moderators/${moderatorId}`, input)
      .then(() => undefined),
  deleteModerator: (workspaceId: number, webinarId: number, moderatorId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/moderators/${moderatorId}`)
      .then(() => undefined),

  // Presenter API (POST, GET, PUT, DELETE, LIST)
  listPresenters: (workspaceId: number, webinarId: number): Promise<Presenter[]> =>
    http
      .get<PresenterPageResponse>(`/workspaces/${workspaceId}/webinars/${webinarId}/presenters`)
      .then((r) => r.data.content),
  createPresenter: async (
    workspaceId: number,
    webinarId: number,
    input: PresenterInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/presenters`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  getPresenter: (workspaceId: number, webinarId: number, presenterId: number): Promise<Presenter> =>
    http
      .get<Presenter>(`/workspaces/${workspaceId}/webinars/${webinarId}/presenters/${presenterId}`)
      .then((r) => r.data),
  updatePresenter: (
    workspaceId: number,
    webinarId: number,
    presenterId: number,
    input: PresenterInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/presenters/${presenterId}`, input)
      .then(() => undefined),
  deletePresenter: (workspaceId: number, webinarId: number, presenterId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/presenters/${presenterId}`)
      .then(() => undefined),

  getWorkspaceAccesses: (workspaceId: number, page = 0, size = 20): Promise<WorkspaceTeamPage> =>
    http
      .get<WorkspaceTeamPage>(`/workspaces/${workspaceId}/accesses`, {
        params: { page, size },
      })
      .then((r) => r.data),

  createWorkspaceInvitation: (
    workspaceId: number,
    input: WorkspaceTeamInviteInput,
    file?: File | null,
  ): Promise<WorkspaceTeamInvitation> => {
    if (file) {
      const formData = new FormData();
      formData.append('input', new Blob([JSON.stringify(input)], { type: 'application/json' }));
      formData.append('file', file);
      return http
        .post<WorkspaceTeamInvitation>(
          `/workspaces/${workspaceId}/accesses/invitations`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then((r) => r.data);
    }
    return http
      .post<WorkspaceTeamInvitation>(`/workspaces/${workspaceId}/accesses/invitations`, input)
      .then((r) => r.data);
  },

  getIntegrationsMe: (): Promise<TechnicalAuthUser> =>
    http.get<TechnicalAuthUser>(`/integrations/me`).then((r) => r.data),

  getIntegrationApiKeys: (workspaceId: number): Promise<IntegrationApiKeyDto[]> =>
    http
      .get<IntegrationApiKeyDto[]>(`/workspaces/${workspaceId}/integrations/api-keys`)
      .then((r) => r.data),

  createIntegrationApiKey: (
    workspaceId: number,
    input: CreateIntegrationApiKeyInput,
  ): Promise<IntegrationApiKeyCreatedDto> =>
    http
      .post<IntegrationApiKeyCreatedDto>(`/workspaces/${workspaceId}/integrations/api-keys`, input)
      .then((r) => r.data),

  rotateIntegrationApiKey: (
    workspaceId: number,
    apiKeyId: string,
  ): Promise<IntegrationApiKeyCreatedDto> =>
    http
      .post<IntegrationApiKeyCreatedDto>(
        `/workspaces/${workspaceId}/integrations/api-keys/${apiKeyId}/rotate`,
      )
      .then((r) => r.data),

  revokeIntegrationApiKey: (workspaceId: number, apiKeyId: string) =>
    http
      .delete(`/workspaces/${workspaceId}/integrations/api-keys/${apiKeyId}`)
      .then(() => undefined),

  getEvaluationTemplate: (workspaceId: number, webinarId: number): Promise<EvaluationTemplate> =>
    http
      .get<EvaluationTemplate>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evaluation-template`,
      )
      .then((r) => r.data),
  getEvaluationQuestions: (workspaceId: number, webinarId: number): Promise<EvaluationQuestion[]> =>
    http
      .get<EvaluationQuestion[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evaluation-template/questions`,
      )
      .then((r) => r.data),
  createEvaluationQuestion: (
    workspaceId: number,
    webinarId: number,
    input: EvaluationQuestionInput,
  ): Promise<void> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/evaluation-template/questions`, input)
      .then(() => undefined),
  updateEvaluationTemplate: (
    workspaceId: number,
    webinarId: number,
    input: EvaluationTemplateInput,
  ): Promise<void> =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/evaluation-template`, input)
      .then(() => undefined),
  updateEvaluationQuestion: (
    workspaceId: number,
    webinarId: number,
    questionId: number,
    input: EvaluationQuestionInput,
  ): Promise<void> =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evaluation-template/questions/${questionId}`,
        input,
      )
      .then(() => undefined),
  deleteEvaluationQuestion: (
    workspaceId: number,
    webinarId: number,
    questionId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evaluation-template/questions/${questionId}`,
      )
      .then(() => undefined),

  listAutomationTriggers: (
    workspaceId: number,
    webinarId: number,
  ): Promise<WebinarIntegrationTrigger[]> =>
    http
      .get<WebinarIntegrationTrigger[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/automation-triggers`,
      )
      .then((r) => r.data),
  createAutomationTrigger: async (
    workspaceId: number,
    webinarId: number,
    input: WebinarIntegrationTriggerInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/automation-triggers`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateAutomationTrigger: (
    workspaceId: number,
    webinarId: number,
    triggerId: number,
    input: WebinarIntegrationTriggerInput,
  ): Promise<void> =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/automation-triggers/${triggerId}`,
        input,
      )
      .then(() => undefined),
  deleteAutomationTrigger: (workspaceId: number, webinarId: number, triggerId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/automation-triggers/${triggerId}`)
      .then(() => undefined),

  // Webhook API
  listWebhooks: (workspaceId: number, webinarId: number): Promise<WebhookDto[]> =>
    http
      .get<WebhookDto[]>(`/workspaces/${workspaceId}/webinars/${webinarId}/webhooks`)
      .then((r) => r.data),
  createWebhook: async (
    workspaceId: number,
    webinarId: number,
    input: WebhookInput,
  ): Promise<number | null> => {
    const response = await http.post(
      `/workspaces/${workspaceId}/webinars/${webinarId}/webhooks`,
      input,
    );
    const locationHeader = response.headers?.location ?? response.headers?.Location;
    if (!locationHeader) {
      return null;
    }

    const idMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
    return idMatch ? Number(idMatch[1]) : null;
  },
  updateWebhook: (workspaceId: number, webinarId: number, webhookId: number, input: WebhookInput) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/webhooks/${webhookId}`, input)
      .then(() => undefined),
  deleteWebhook: (workspaceId: number, webinarId: number, webhookId: number) =>
    http
      .delete(`/workspaces/${workspaceId}/webinars/${webinarId}/webhooks/${webhookId}`)
      .then(() => undefined),

  getParticipationStatuses: (
    workspaceId: number,
    webinarId: number,
  ): Promise<WebinarParticipationStatusConfigDto> =>
    http
      .get<WebinarParticipationStatusConfigDto>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/participation-statuses`,
      )
      .then((r) => r.data),
  replaceParticipationStatuses: (
    workspaceId: number,
    webinarId: number,
    input: ParticipationStatusBulkInput,
  ) =>
    http
      .put(`/workspaces/${workspaceId}/webinars/${webinarId}/participation-statuses`, input)
      .then(() => undefined),
  async testWebhook(workspaceId: number, webinarId: number, webhookId: number): Promise<void> {
    const { data } = await http.post<void>(
      `/workspaces/${workspaceId}/webinars/${webinarId}/webhooks/${webhookId}/test`,
    );
    return data;
  },
  async getOfferTestButtonLink(
    workspaceId: number,
    webinarId: number,
    templateId: number,
    contentType: string,
  ): Promise<OfferLinkTestDto> {
    const { data } = await http.post<OfferLinkTestDto>(
      `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${templateId}/contents/${contentType}/test-link`,
    );
    return data;
  },

  // Contenu tel que le verra le spectateur (variables remplacées par John Doe), sans analytics.
  async getOfferTestEmbed(
    workspaceId: number,
    webinarId: number,
    templateId: number,
    contentType: string,
  ): Promise<OfferTemplateContent> {
    const { data } = await http.post<OfferTemplateContent>(
      `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${templateId}/contents/${contentType}/test-embed`,
    );
    return data;
  },

  listSharedResources: (
    workspaceId: number,
    webinarId: number,
    page = 0,
    size = 20,
  ): Promise<SharedResourcePage> =>
    http
      .get<{
        content: SharedResource[];
        number: number;
        size: number;
        totalPages: number;
        totalElements: number;
      }>(`/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources`, {
        params: { page, size },
      })
      .then((r) => ({
        content: r.data.content ?? [],
        page: r.data.number,
        size: r.data.size,
        totalPages: r.data.totalPages,
        totalElements: r.data.totalElements,
      })),

  initSharedResourceFileUpload: (
    workspaceId: number,
    webinarId: number,
    input: SharedResourceFileUploadInitInput,
  ): Promise<SharedResourceUploadInit> =>
    http
      .post<SharedResourceUploadInit>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources/files/upload-init`,
        input,
      )
      .then((r) => r.data),

  finalizeSharedResourceFileUpload: (
    workspaceId: number,
    webinarId: number,
    sharedResourceId: number,
  ): Promise<SharedResource> =>
    http
      .post<SharedResource>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources/${sharedResourceId}/finalize`,
      )
      .then((r) => r.data),

  createSharedResourceLink: (
    workspaceId: number,
    webinarId: number,
    input: SharedResourceLinkInput,
  ): Promise<SharedResource> =>
    http
      .post<SharedResource>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources/links`,
        input,
      )
      .then((r) => r.data),

  deleteSharedResource: (
    workspaceId: number,
    webinarId: number,
    sharedResourceId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources/${sharedResourceId}`,
      )
      .then(() => undefined),

  adminShowSharedResource: (
    workspaceId: number,
    webinarId: number,
    sessionIdentifier: string,
    sharedResourceId: number,
  ): Promise<SharedResourcePublic> =>
    http
      .post<SharedResourcePublic>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources/sessions/${sessionIdentifier}/${sharedResourceId}/show`,
      )
      .then((r) => r.data),

  adminHideSharedResource: (
    workspaceId: number,
    webinarId: number,
    sessionIdentifier: string,
    sharedResourceId: number,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/shared-resources/sessions/${sessionIdentifier}/${sharedResourceId}/hide`,
      )
      .then(() => undefined),

  getCurrentSharedResources: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
  ): Promise<SharedResourcePublic[]> =>
    getPublicRead<SharedResourcePublic[]>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/shared-resources/current`,
      { authorization },
    ),

  getSharedResourceDownloadUrl: (
    webinarId: number,
    sessionIdentifier: string,
    sharedResourceId: number,
    authorization: string,
  ): Promise<SharedResourceDownloadUrl> =>
    http
      .post<SharedResourceDownloadUrl>(
        `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/shared-resources/${sharedResourceId}/download-url`,
        undefined,
        { headers: { Authorization: authorization } },
      )
      .then((r) => r.data),

  startEvergreenTestRun: (workspaceId: number, webinarId: number): Promise<EvergreenTestRun> =>
    http
      .post<EvergreenTestRun>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/test-runs`,
        undefined,
      )
      .then((r) => r.data),

  getEvergreenTestRuntime: (
    workspaceId: number,
    webinarId: number,
    testRunId: string,
  ): Promise<EvergreenTestRuntime> =>
    http
      .get<EvergreenTestRuntime>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/test-runs/${testRunId}`,
      )
      .then((r) => r.data),

  getEvergreenTestSharedResourceDownloadUrl: (
    workspaceId: number,
    webinarId: number,
    testRunId: string,
    sharedResourceId: number,
  ): Promise<SharedResourceDownloadUrl> =>
    http
      .post<SharedResourceDownloadUrl>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/evergreen/test-runs/${testRunId}/shared-resources/${sharedResourceId}/download-url`,
        undefined,
      )
      .then((r) => r.data),
};
